import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { FormFieldErrorsState } from '../../shared/design-system/form-field-errors';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { MarketplaceSellerSelectorComponent } from '../../shared/design-system/marketplace-seller-selector.component';
import { OfferingSelectorComponent } from '../../shared/design-system/offering-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  CatalogService,
  MarketplaceCommissionRuleRecord,
  MarketplaceCommissionSimulationResult
} from './catalog.service';
import {
  MarketplaceLiveSubscription,
  subscribeMarketplaceOperationsLiveUpdates,
  unsubscribeMarketplaceOperationsLiveUpdates
} from './marketplace-list-page.utils';

@Component({
  standalone: true,
  selector: 'ain-marketplace-commission-rules',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    AinFormFieldComponent,
    AinAmountPipe,
    TableModule,
    ButtonModule,
    DialogModule,
    FormsModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    RouterLink,
    MarketplaceSellerSelectorComponent,
    OfferingSelectorComponent
  ],
  template: `
    <ain-page [title]="t('catalog.marketplace.commissionRules.title')" [description]="t('catalog.marketplace.commissionRules.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'catalog.marketplace.commissionRules.help.title'"
        [descriptionKey]="'catalog.marketplace.commissionRules.help.description'"
        [bulletKeys]="[
          'catalog.marketplace.commissionRules.help.bullets.scope',
          'catalog.marketplace.commissionRules.help.bullets.snapshot',
          'catalog.marketplace.commissionRules.help.bullets.simulator'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <a class="link" routerLink="/marketplace/dashboard">{{ t('catalog.action.backToList') }}</a>
            <button pButton type="button" [label]="t('catalog.marketplace.commissionRules.create')" (click)="openCreate()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.commissionRules.migrate')" [disabled]="acting()" (click)="migrate()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.action.refresh')" (click)="load()"></button>
          </div>
          <p-table [value]="rows()" dataKey="id" [paginator]="true" [rows]="15">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.marketplace.commissionRules.fields.code') }}</th>
                <th>{{ t('catalog.marketplace.commissionRules.fields.name') }}</th>
                <th>{{ t('catalog.marketplace.commissionRules.fields.scope') }}</th>
                <th>{{ t('catalog.marketplace.commissionRules.fields.type') }}</th>
                <th>{{ t('catalog.marketplace.commissionRules.fields.rate') }}</th>
                <th>{{ t('catalog.marketplace.commissionRules.fields.priority') }}</th>
                <th>{{ t('columns.status') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.ruleCode }}</td>
                <td>{{ row.name }}</td>
                <td>{{ row.scopeKind }}</td>
                <td>{{ row.ruleType }}</td>
                <td>{{ row.ruleType === 'PercentOfLineGross' ? row.ratePercent + '%' : (row.fixedAmount | ainAmount: 2) }}</td>
                <td>{{ row.priority }}</td>
                <td>{{ row.isActive ? t('common.active') : t('common.inactive') }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>

        <section class="panel">
          <h3>{{ t('catalog.marketplace.commissionRules.simulator.title') }}</h3>
          <p class="muted">{{ t('catalog.marketplace.commissionRules.simulator.description') }}</p>
          <div class="sim-grid">
            <ain-form-field [label]="t('catalog.marketplace.commissionRules.simulator.lineGross')" [errorKey]="fieldErrors.errorKey('simGross')">
              <p-inputNumber [(ngModel)]="simLineGross" (ngModelChange)="fieldErrors.clear('simGross')" [minFractionDigits]="2" />
            </ain-form-field>
            <ain-form-field [label]="t('catalog.marketplace.commissionRules.simulator.seller')">
              <ain-marketplace-seller-selector [(ngModel)]="simSellerId" />
            </ain-form-field>
            <ain-form-field [label]="t('catalog.marketplace.commissionRules.simulator.offering')">
              <ain-offering-selector [(ngModel)]="simOfferingId" />
            </ain-form-field>
            <ain-form-field [label]="t('catalog.marketplace.commissionRules.simulator.store')">
              <input pInputText [(ngModel)]="simStoreId" />
            </ain-form-field>
          </div>
          <button pButton type="button" [label]="t('catalog.marketplace.commissionRules.simulator.run')" [disabled]="acting()" (click)="runSimulation()"></button>
          @if (simResult()) {
            <p class="result">{{ t('catalog.marketplace.commissionRules.simulator.total') }}: {{ simResult()!.totalCommissionAmount | ainAmount: 2 }}</p>
            <p-table [value]="simResult()!.lines">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('catalog.marketplace.commissionRules.simulator.line') }}</th>
                  <th>{{ t('catalog.marketplace.commissionRules.simulator.commission') }}</th>
                  <th>{{ t('catalog.marketplace.commissionRules.fields.code') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-line>
                <tr>
                  <td>{{ line.lineNumber }}</td>
                  <td>{{ line.commissionAmount | ainAmount: 2 }}</td>
                  <td>{{ line.snapshot.ruleCode }}</td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>
      }
    </ain-page>

    <p-dialog [header]="t('catalog.marketplace.commissionRules.createTitle')" [(visible)]="createVisible" [modal]="true" [style]="{ width: '520px' }">
      <div class="sim-grid">
        <ain-form-field [label]="t('catalog.marketplace.commissionRules.fields.code')" [errorKey]="fieldErrors.errorKey('ruleCode')">
          <input pInputText [(ngModel)]="draft.ruleCode" (ngModelChange)="fieldErrors.clear('ruleCode')" />
        </ain-form-field>
        <ain-form-field [label]="t('catalog.marketplace.commissionRules.fields.name')" [errorKey]="fieldErrors.errorKey('name')">
          <input pInputText [(ngModel)]="draft.name" (ngModelChange)="fieldErrors.clear('name')" />
        </ain-form-field>
        <ain-form-field [label]="t('catalog.marketplace.commissionRules.fields.scope')">
          <p-select [options]="scopeOptions" [(ngModel)]="draft.scopeKind" optionLabel="label" optionValue="value" appendTo="body" />
        </ain-form-field>
        <ain-form-field [label]="t('catalog.marketplace.commissionRules.fields.type')">
          <p-select [options]="typeOptions" [(ngModel)]="draft.ruleType" optionLabel="label" optionValue="value" appendTo="body" />
        </ain-form-field>
        @if (draft.ruleType === 'PercentOfLineGross') {
          <ain-form-field [label]="t('catalog.marketplace.commissionRules.fields.rate')">
            <p-inputNumber [(ngModel)]="draft.ratePercent" suffix="%" [minFractionDigits]="2" />
          </ain-form-field>
        } @else {
          <ain-form-field [label]="t('catalog.marketplace.commissionRules.fields.fixed')">
            <p-inputNumber [(ngModel)]="draft.fixedAmount" [minFractionDigits]="2" />
          </ain-form-field>
        }
        <ain-form-field [label]="t('catalog.marketplace.commissionRules.fields.priority')">
          <p-inputNumber [(ngModel)]="draft.priority" />
        </ain-form-field>
      </div>
      <ng-template pTemplate="footer">
        <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="createVisible = false"></button>
        <button pButton type="button" [label]="t('common.save')" [disabled]="acting()" (click)="saveCreate()"></button>
      </ng-template>
    </p-dialog>
  `,
  styles: [
    `.panel{display:grid;gap:12px;margin-bottom:20px}.toolbar{display:flex;gap:8px;flex-wrap:wrap;align-items:center}.link{color:#1d4ed8}.sim-grid{display:grid;gap:12px;grid-template-columns:repeat(auto-fit,minmax(200px,1fr))}.muted{color:#64748b}.result{font-weight:600;margin-top:8px}`
  ]
})
export class MarketplaceCommissionRulesComponent implements OnInit, OnDestroy {
  private readonly api = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly liveUpdates = inject(LiveUpdateService);
  private readonly context = inject(CurrentContextService);
  readonly fieldErrors = new FormFieldErrorsState();
  readonly loading = signal(true);
  readonly acting = signal(false);
  readonly rows = signal<MarketplaceCommissionRuleRecord[]>([]);
  readonly simResult = signal<MarketplaceCommissionSimulationResult | null>(null);
  createVisible = false;
  simLineGross = 100;
  simSellerId: string | null = null;
  simOfferingId: string | null = null;
  simStoreId = '';
  private liveSub: MarketplaceLiveSubscription | null = null;

  draft = {
    ruleCode: '',
    name: '',
    scopeKind: 'TenantDefault',
    ruleType: 'PercentOfLineGross',
    ratePercent: 10,
    fixedAmount: 0,
    priority: 0
  };

  scopeOptions = [
    'TenantDefault',
    'Store',
    'Category',
    'Seller',
    'SellerCategory',
    'Offering',
    'SellerOffering'
  ].map(value => ({ value, label: value }));

  typeOptions = ['PercentOfLineGross', 'FixedPerLine', 'FixedPerOrder'].map(value => ({ value, label: value }));

  ngOnInit(): void {
    this.load();
    void this.startLiveUpdates();
  }

  ngOnDestroy(): void {
    void unsubscribeMarketplaceOperationsLiveUpdates(this.liveUpdates, this.liveSub ?? { topic: null, eventsSubscription: null });
  }

  private async startLiveUpdates(): Promise<void> {
    this.liveSub = await subscribeMarketplaceOperationsLiveUpdates(
      this.context.context()?.tenantId,
      this.liveUpdates,
      () => this.load(false));
  }

  load(showSpinner = true): void {
    if (showSpinner) this.loading.set(true);
    this.api.listMarketplaceCommissionRules().subscribe({
      next: items => {
        this.rows.set(items);
        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
  }

  openCreate(): void {
    this.createVisible = true;
    this.draft = {
      ruleCode: '',
      name: '',
      scopeKind: 'TenantDefault',
      ruleType: 'PercentOfLineGross',
      ratePercent: 10,
      fixedAmount: 0,
      priority: 0
    };
  }

  saveCreate(): void {
    if (!this.fieldErrors.validate([
      { field: 'ruleCode', value: () => this.draft.ruleCode, messageKey: 'common.validation.required' },
      { field: 'name', value: () => this.draft.name, messageKey: 'common.validation.required' }
    ])) {
      return;
    }
    this.acting.set(true);
    this.api.createMarketplaceCommissionRule({
      ruleCode: this.draft.ruleCode.trim(),
      name: this.draft.name.trim(),
      scopeKind: this.draft.scopeKind,
      ruleType: this.draft.ruleType,
      ratePercent: this.draft.ratePercent,
      fixedAmount: this.draft.fixedAmount,
      minLineGross: 0,
      maxCommissionAmount: null,
      priority: this.draft.priority,
      effectiveFromUtc: new Date().toISOString(),
      effectiveUntilUtc: null,
      storeId: null,
      sellerId: null,
      categoryId: null,
      offeringId: null
    }).subscribe({
      next: () => {
        this.createVisible = false;
        this.acting.set(false);
        this.load(false);
      },
      error: () => this.acting.set(false)
    });
  }

  migrate(): void {
    this.acting.set(true);
    this.api.migrateMarketplaceCommissionFromPolicies().subscribe({
      next: () => {
        this.acting.set(false);
        this.load(false);
      },
      error: () => this.acting.set(false)
    });
  }

  runSimulation(): void {
    if (!this.fieldErrors.validate([
      { field: 'simGross', value: () => (this.simLineGross > 0 ? 'ok' : ''), messageKey: 'common.validation.required' }
    ])) {
      return;
    }
    if (!this.simOfferingId) {
      return;
    }
    this.acting.set(true);
    this.api.simulateMarketplaceCommission({
      asOfUtc: null,
      lines: [{
        lineNumber: 1,
        lineGrossAmount: this.simLineGross,
        sellerId: this.simSellerId,
        offeringId: this.simOfferingId,
        categoryId: null,
        storeId: this.simStoreId.trim() || null
      }]
    }).subscribe({
      next: result => {
        this.simResult.set(result);
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
